// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getLang } from 'redux-pagan';
import { change } from 'redux-form';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
// Components
import PhoneVerificationFormMyAccount from '../../components/PhoneVerificationMyAccount/PhoneVerificationFormMyAccount';
import PhoneVerificationForm from '../../components/PhoneVerification/PhoneVerificationForm';
// Helpers
import { requestErrorStatusCodes } from '../../constants';
import { trackEvent } from '../../utils/analytics/analyticsManager';
import { eventCategory, eventAction } from '../../utils/analytics/analyticsConstants';
// Redux
import { sendPhoneNumber, verifyCode } from '../../redux/modules/phoneSmsVerification';
import { openMessageModal, hideMessageModal } from '../../redux/modules/messageModal';
import { editUserProfile } from '../../redux/modules/userAuthentication';
import checkRtlLanguage from '../../utils/checkRtlLanguage';
import { authenticationFormatPhoneNumber, normalizePhoneAndCountryCode } from '../../utils/formatPhoneNumber';

const changeValue = (name, value) => change('editProfileForm', name, value);

const requiredFields = ['phone', 'reCaptcha'];

const formatRules = {
  default: {
    6: '-'
  },
  dash: {
    6: '-',
    8: ' '
  }
};

const normilize = (number = '') => number.toString().replace(/\D/g, '');

const getFormatRules = inputLength => ((inputLength > 10)
  ? formatRules.dash
  : formatRules.default
);

const formatNum = (number) => {
  if (!number) {
    return '';
  }

  const reverseRow = number.toString().replace(/\D/g, '').split('').reverse();
  const numberLen = reverseRow.length;
  const rules = getFormatRules(numberLen);

  return reverseRow.reduce((acc, num, idx) => {
    const prefix = rules[idx] || '';

    return `${prefix}${num}${acc}`;
  }, '');
};

class PhoneVerificationContainer extends Component {
  state = {
    countryCode: '',
    data: {},
    invalidFields: [],
    codeIsValid: true,
    codeIsVerified: false,
    phoneNumberIsInvalid: false,
    showCodePreloader: false,
    phoneNumberSent: false,
    errorMessage: '',
    showErrorMessage: false,
    doResetCaptcha: false,
    awaitingCaptcha: false,
    phoneNumberAlreadyTaken: false
  };

  componentDidMount() {
    const phoneNumber = get(this.props, 'userAuthenticationData.phone_number');
    if (phoneNumber && phoneNumber.charAt(0) === '0') {
      phoneNumber.slice(1);
    }

    if (phoneNumber) {
      this.savePhoneNumber(`${phoneNumber.length > 10 ? '+' : ''}${phoneNumber}`);
    }
  }

  componentWillReceiveProps = ({
    userAuthenticationData: { phone_number: phoneNumber },
    phoneSmsVerification: smsVerify,
    locale,
    translate
  }) => {
    const stateModifier = {};

    if (smsVerify) {
      if (smsVerify.sendingPhoneNumber !== this.props.phoneSmsVerification.sendingPhoneNumber) {
        stateModifier.showPhonePreloader = smsVerify.sendingPhoneNumber;
      }

      if (smsVerify.phoneNumberSent !== this.props.phoneSmsVerification.phoneNumberSent) {
        stateModifier.phoneNumberSent = smsVerify.phoneNumberSent;
      }

      if (
        this.props.isPublishForm
        && this.props.phoneSmsVerification.sendingPhoneNumber
        && !smsVerify.sendingPhoneNumber
      ) {
        stateModifier.doResetCaptcha = true;
      }

      if (smsVerify.phoneError && !isEqual(smsVerify.phoneError, this.props.phoneSmsVerification.phoneError)) {
        stateModifier.doResetCaptcha = true;
        // analytics
        trackEvent(eventCategory.error, eventAction.validation, smsVerify.phoneError.message);

        if (smsVerify.phoneError.status === requestErrorStatusCodes.BAD_REQUEST) {
          stateModifier.showErrorMessage = true;
          stateModifier.phoneNumberAlreadyTaken = true;
          if (smsVerify.phoneError.message === 'EXCEEDED_LIMIT_PER_MINUTE') {
            stateModifier.errorMessage = `${this.props.translate('phoneVerification', 'exceededLimitPerMinuteError')}`;
          } else if (smsVerify.phoneError.message === 'EXCEEDED_LIMIT_PER_DAY') {
            stateModifier.errorMessage = `${this.props.translate('phoneVerification', 'exceededLimitPerDayError')}`;
          } else {
            stateModifier.errorMessage = `${this.props.translate('phoneVerification', 'phoneWasVerifiedError')}`;
          }
        } else {
          stateModifier.phoneNumberIsInvalid = true;
          this.props.openMessageModal({ message: (
            <div className="contentMessage">
              <div>
                <p>{this.props.translate('phoneVerification', 'smsWasNotSentError')}</p>
                <button className="btn btn-primary" onClick={this.props.hideMessageModal}>{this.props.translate('phoneVerification', 'tryAgain')}</button>
              </div>
            </div>)
          });
        }
      } else if (smsVerify.phoneError === null && smsVerify.phoneError !== this.props.phoneSmsVerification.phoneError) {
        stateModifier.phoneNumberIsInvalid = false;
        stateModifier.showErrorMessage = false;
        stateModifier.doResetCaptcha = false;
      }

      // Update error message after locale change
      if (
        smsVerify.phoneError
        && smsVerify.phoneError.status === requestErrorStatusCodes.BAD_REQUEST
        && locale !== this.props.locale
      ) {
        stateModifier.errorMessage = translate('phoneVerification', 'phoneWasVerifiedError').toString();
      }

      if (smsVerify.codeIsValid !== this.props.phoneSmsVerification.codeIsValid) {
        stateModifier.codeIsVerified = smsVerify.codeIsValid;
        this.props.verificationSuccess(smsVerify.codeIsValid);
        this.props.editUserProfile({
          userData: { phone_number: `${this.state.countryCode}${this.state.data.phone}` },
          userToken: this.props.userAuthenticationData.access_token
        });
      }

      if (smsVerify.codeError && !isEqual(smsVerify.codeError, this.props.phoneSmsVerification.codeError)) {
        stateModifier.codeIsValid = false;
        stateModifier.codeIsVerified = false;
      }

      if (smsVerify.sendingVerificationCode !== this.props.phoneSmsVerification.sendingVerificationCode) {
        stateModifier.showCodePreloader = smsVerify.sendingVerificationCode;
      }
    }

    if (Object.keys(stateModifier).length) {
      this.setState(stateModifier);
    }
  };

  getPhoneForCaptcha = () => {
    const { phone } = this.state.data;

    if (!phone) {
      return undefined;
    }

    return this.props.isPublishForm
      ? authenticationFormatPhoneNumber(phone)
      : normalizePhoneAndCountryCode(phone, this.state.countryCode);
  };

  savePhoneNumber = (phoneNumber) => {
    this.initialPhoneNumber = phoneNumber;

    if (this.props.isMyAccount && this.initialPhoneNumber) {
      this.saveData('phone', this.initialPhoneNumber);
    }
  };

  validateForm = (evt) => {
    if (evt) {
      evt.preventDefault();
    }

    const invalidFields = requiredFields.filter((item) => {
      switch (item) {
        case 'phone': {
          return !this.validatePhoneNumber(this.state.data.phone);
        }
        default: {
          return !this.state.data[item];
        }
      }
    });

    const phoneNumberIsInvalid = invalidFields.includes('phone');
    const errorMessage = phoneNumberIsInvalid
      ? this.props.translate('phoneVerification', 'phoneErrorMessage').toString() : '';

    const captchaPending = !!this.props.isPublishForm
      && !phoneNumberIsInvalid
      && invalidFields.includes('reCaptcha');

    this.setState({
      invalidFields,
      errorMessage,
      phoneNumberIsInvalid,
      showErrorMessage: !!invalidFields.length && !captchaPending,
      awaitingCaptcha: captchaPending
    });

    if (!invalidFields.length) {
      this.submitForm();
    } else if (captchaPending) {
      this.pendingSubmit = true;
    }
    return true;
  };

  validatePhoneNumber = (phoneNumber) => {
    const number = normilize(phoneNumber);
    const isValid = number && number.length >= 9 && number.length <= 12;

    this.setState({ phoneNumberIsInvalid: !isValid });
    return isValid;
  };

  verifyCode = (code) => {
    const { verificationCodeSent, sendingVerificationCode } = this.props.phoneSmsVerification;
    if (this.validateCode(code) && !verificationCodeSent && !sendingVerificationCode) {
      this.setState({ codeIsValid: true });
      const { access_token: accessToken } = this.props.userAuthenticationData;
      this.props.verifyCode({ code, accessToken });
    }
  };

  validateCode = code => (code.length === 4);

  submitForm = () => {
    const { countryCode, data: { reCaptcha, phone } } = this.state;
    const { access_token: accessToken } = this.props.userAuthenticationData;
    const formatPhone = this.props.isPublishForm
      ? authenticationFormatPhoneNumber(phone)
      : normalizePhoneAndCountryCode(phone, countryCode);

    this.props.sendPhoneNumber({
      captcha: reCaptcha,
      phone: formatPhone,
      locale: this.props.locale,
      accessToken
    });

    if (this.props.isPublishForm) {
      this.setState({
        doResetCaptcha: false,
        awaitingCaptcha: false,
        data: { ...this.state.data, reCaptcha: null }
      });
    }
  };

  handlePhoneChange = (evt, value = '', flag) => {
    const normalizedValue = value.replace(/\s/g, '');

    this.savePhoneNumber(normalizedValue);
    this.saveData('phone', normalizedValue);

    if (`+${flag.dialCode}` !== this.state.countryCode) {
      this.setState({ countryCode: `+${flag.dialCode}` });
    }

    if (this.props.isMyAccount) {
      this.props.changeValue('phone_number', `+${flag.dialCode}${normalizedValue}`);
    }
    if (this.props.isPublishForm) {
      this.props.onPhoneChange(`+${flag.dialCode}${normalizedValue}`);
    }
  }

  handleChange = (evt) => {
    const { name, value } = evt.target;
    const normalizedValue = value.replace(/[\D]/g, '');

    if (name === 'phone') {
      evt.target.value = (this.props.isMobile) ? normalizedValue : formatNum(normalizedValue);

      if (this.props.isMyAccount) {
        this.props.changeValue('phone_number', evt.target.value);
      }

      if (this.props.isPublishForm) {
        this.props.onPhoneChange(normalizedValue);
      }
    } else {
      evt.target.value = normalizedValue;
    }

    if (name === 'verificationCode') {
      this.verifyCode(normalizedValue);
    }

    this.saveData(name, normalizedValue);
  };

  fetchReCaptchaToken = (token) => {
    const stateModifier = {};

    if (token && this.state.showErrorMessage) {
      stateModifier.showErrorMessage = false;
    }

    if (token || this.props.isPublishForm) {
      stateModifier.reCaptcha = token || null;
    }

    this.setState({
      data: {
        ...this.state.data,
        ...stateModifier
      }
    }, () => {
      if (token && this.pendingSubmit) {
        this.pendingSubmit = false;
        this.submitForm();
      }
    });
  };

  saveData = (name, value) => this.setState({
    data: {
      ...this.state.data,
      [name]: value
    }
  });

  render() {
    const Form = (this.props.isMyAccount) ? PhoneVerificationFormMyAccount : PhoneVerificationForm;

    return (
      <form id="verifyPhoneForm" onSubmit={this.validateForm}>
        <Form
          translate={this.props.translate('phoneVerification')}
          locale={this.props.locale}
          isMobile={this.props.isMobile}
          phoneForCaptcha={this.getPhoneForCaptcha()}
          tooltipMsg={`${this.props.translate('phoneVerification', 'phoneVerificationTootlipMessage')}`}
          validateCode={this.validateCode}
          validatePhoneNumber={this.validatePhoneNumber}
          phoneNumberIsInvalid={this.state.phoneNumberIsInvalid}
          sendPhoneNumber={this.validateForm}
          phoneNumberSent={this.state.phoneNumberSent}
          codeIsValid={this.state.codeIsValid}
          codeIsVerified={this.state.codeIsVerified}
          // phoneNumberWasVerified={this.state.phoneNumberWasVerified}
          showCodePreloader={this.state.showCodePreloader}
          showPhonePreloader={this.state.showPhonePreloader || this.state.awaitingCaptcha}
          fetchReCaptchaToken={this.fetchReCaptchaToken}
          onChange={this.handleChange}
          onPhoneChange={this.handlePhoneChange}
          errorMessage={this.state.errorMessage}
          showErrorMessage={this.state.showErrorMessage}
          doResetCaptcha={this.state.doResetCaptcha}
          phoneNumberAlreadyTaken={this.state.phoneNumberAlreadyTaken}
          isRtl={this.props.isRtl}
          initialPhoneValue={this.initialPhoneNumber}
          phoneIsVerified={this.props.phoneIsVerified || this.props.isVerified}
        />
      </form>
    );
  }
}

PhoneVerificationContainer.propTypes = {
  locale: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  translate: PropTypes.func.isRequired,
  userAuthenticationData: PropTypes.object,
  phoneSmsVerification: PropTypes.object,
  sendPhoneNumber: PropTypes.func,
  verifyCode: PropTypes.func,
  verificationSuccess: PropTypes.func,
  openMessageModal: PropTypes.func,
  hideMessageModal: PropTypes.func,
  editUserProfile: PropTypes.func.isRequired,
  isRtl: PropTypes.bool,
  phoneIsVerified: PropTypes.bool,
  changeValue: PropTypes.func,
  isMyAccount: PropTypes.bool,
  isPublishForm: PropTypes.bool,
  onPhoneChange: PropTypes.func,
  isVerified: PropTypes.bool
};

const mapStateToProps = ({
  lang,
  windowDimentions,
  userAuthentication,
  phoneSmsVerification
}) => ({
  locale: lang.locale,
  isRtl: checkRtlLanguage(lang.locale),
  translate: getLang(lang, 'static'),
  isMobile: windowDimentions.isMobile,
  phoneIsVerified: get(userAuthentication, 'data.verification.phone'),
  userAuthenticationData: userAuthentication.data || {},
  phoneSmsVerification
});

const mapDispatchToProps = {
  sendPhoneNumber,
  verifyCode,
  openMessageModal,
  hideMessageModal,
  editUserProfile,
  changeValue
};

export default connect(mapStateToProps, mapDispatchToProps)(
  PhoneVerificationContainer
);
